// FILE: src/components/ExamBreak.js
// PMI-style scheduled break overlay for Simulation / Full Exam mode.
//
// Two phases, both rendered as an OPAQUE full-screen overlay so no question
// content (or answers) is visible behind it:
//
//   'offered'  The learner has just moved past a break threshold (e.g. PMP
//              question 60). Shows the scheduled-break explainer with
//              [Start Break] / [Skip Break].
//
//   'active'   The break is running. Shows a large mm:ss countdown that
//              ticks down from durationMinutes to 00:00, plus [Resume Exam].
//              When the countdown reaches zero the exam resumes
//              automatically via onResume.
//
// The countdown is WALL-CLOCK based: remaining time is recomputed every tick
// from `breakStartedAt` (epoch ms, owned by App.js and persisted in session
// state), so a re-render — or a future session-restore after a page refresh —
// always lands on the correct remaining time rather than restarting at 10:00.
//
// This component owns no break state beyond its display tick. All decisions
// (when a break is offered, locking, exam-clock pause) live in App.js, and
// every threshold/duration comes from exams.js config via getExamBreaks().

import React, { useState, useEffect, useRef } from 'react';
import CoachAvatar from '../coach/CoachAvatar';

/** Format seconds as MM:SS (e.g. 599 → "09:59"). */
function formatBreakTime(totalSeconds) {
  const clamped = Math.max(0, totalSeconds);
  const m = Math.floor(clamped / 60);
  const s = clamped % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Seconds remaining in the break, derived from the wall clock. */
function remainingSeconds(breakStartedAt, durationMinutes) {
  const elapsed = Math.floor((Date.now() - breakStartedAt) / 1000);
  return durationMinutes * 60 - elapsed;
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    // Opaque on purpose — a scheduled break must not reveal the next
    // question (or any prior answers) behind it.
    background: '#f4f6f8',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
  },
  card: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
    padding: '36px 44px',
    maxWidth: '620px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '28px',
  },
  // Mascot column — separate column beside the break content, sized to span
  // the full height of the adjacent text block (Bryan, 2026-06-11).
  avatarCol: {
    flexShrink: 0,
    alignSelf: 'center',
  },
  contentCol: {
    flex: 1,
    minWidth: 0,
    textAlign: 'center',
  },
  heading: {
    margin: '0 0 14px',
    fontSize: '1.5rem',
    color: '#1a1a2e',
  },
  body: {
    margin: '0 0 18px',
    color: '#444',
    fontSize: '0.98rem',
    lineHeight: 1.55,
  },
  remainingBadge: {
    display: 'inline-block',
    background: '#eef2f7',
    borderRadius: '999px',
    padding: '6px 16px',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#1a1a2e',
    marginBottom: '18px',
  },
  bulletList: {
    textAlign: 'left',
    margin: '0 auto 24px',
    paddingLeft: '22px',
    color: '#555',
    fontSize: '0.92rem',
    lineHeight: 1.7,
    maxWidth: '340px',
  },
  countdown: {
    fontSize: '4rem',
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    color: '#1a1a2e',
    margin: '8px 0 24px',
    letterSpacing: '2px',
  },
  countdownWarning: {
    color: '#c97a00',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
  },
  primaryButton: {
    padding: '12px 28px',
    fontSize: '1rem',
    fontWeight: 600,
    background: '#1a1a2e',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '12px 28px',
    fontSize: '1rem',
    fontWeight: 600,
    background: '#fff',
    color: '#555',
    border: '1px solid #999',
    borderRadius: '8px',
    cursor: 'pointer',
  },
};

/**
 * @param {'offered'|'active'} phase
 * @param {number}   breakNumber     1-based index of this break (1 = first break)
 * @param {number}   totalBreaks     total scheduled breaks for this cert
 * @param {number}   durationMinutes max break length from exams.js config
 * @param {number}   [breakStartedAt] epoch ms when the break began (phase 'active')
 * @param {function} onStart         learner chose [Start Break]
 * @param {function} onSkip          learner chose [Skip Break]
 * @param {function} onResume        learner clicked [Resume Exam] OR countdown hit zero
 * @param {React.ReactNode} [pacePanel] optional pace coaching panel rendered
 *                                       above the actions (Intelligent Pace Coaching)
 */
export default function ExamBreak({
  phase,
  breakNumber,
  totalBreaks,
  durationMinutes,
  breakStartedAt,
  onStart,
  onSkip,
  onResume,
  pacePanel,
}) {
  // Display tick for the active countdown. Value derives from the wall clock,
  // not from decrementing state, so it survives re-renders and restores.
  const [secondsLeft, setSecondsLeft] = useState(() =>
    phase === 'active' && breakStartedAt
      ? remainingSeconds(breakStartedAt, durationMinutes)
      : durationMinutes * 60
  );

  // Latest onResume without re-subscribing the interval.
  const onResumeRef = useRef(onResume);
  useEffect(() => {
    onResumeRef.current = onResume;
  });

  // ── Countdown tick (active phase only) ─────────────────────────────────────
  useEffect(() => {
    if (phase !== 'active' || !breakStartedAt) return undefined;

    setSecondsLeft(remainingSeconds(breakStartedAt, durationMinutes));
    const interval = setInterval(() => {
      setSecondsLeft(remainingSeconds(breakStartedAt, durationMinutes));
    }, 1000);

    return () => clearInterval(interval);
  }, [phase, breakStartedAt, durationMinutes]);

  // ── Auto-resume at 00:00 ───────────────────────────────────────────────────
  // Fired from an effect (never inside a setState updater) and guarded so it
  // runs exactly once per break.
  const resumedRef = useRef(false);
  useEffect(() => {
    if (phase === 'active' && secondsLeft <= 0 && !resumedRef.current) {
      resumedRef.current = true;
      onResumeRef.current?.();
    }
  }, [phase, secondsLeft]);

  const remainingBreaks = Math.max(0, totalBreaks - breakNumber);

  // ── Offer screen ───────────────────────────────────────────────────────────
  if (phase === 'offered') {
    return (
      <div style={styles.overlay} data-testid="break-offer-screen" role="dialog" aria-label="Scheduled break">
        <div style={styles.card}>
          <div style={styles.avatarCol}>
            <CoachAvatar pose="break" heightPx={210} loading="eager" />
          </div>
          <div style={styles.contentCol}>
          <h2 style={styles.heading}>Scheduled Break</h2>
          <p style={styles.body}>
            Nice work — you have reached a scheduled exam break.
            You may take up to {durationMinutes} minutes.
          </p>
          <div style={styles.remainingBadge} data-testid="remaining-breaks">
            Remaining breaks after this one: {remainingBreaks} of {totalBreaks}
          </div>
          <ul style={styles.bulletList}>
            <li>The exam timer is paused</li>
            <li>Previous questions can no longer be modified</li>
            <li>The exam will resume automatically when break time expires</li>
          </ul>
          {pacePanel}
          <div style={styles.actions}>
            <button data-testid="start-break-button" style={styles.primaryButton} onClick={onStart}>
              Start Break
            </button>
            <button data-testid="skip-break-button" style={styles.secondaryButton} onClick={onSkip}>
              Skip Break
            </button>
          </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Active countdown ───────────────────────────────────────────────────────
  const warning = secondsLeft <= 60;
  return (
    <div style={styles.overlay} data-testid="break-countdown-screen" role="dialog" aria-label="Break in progress">
      <div style={styles.card}>
        <div style={styles.avatarCol}>
          <CoachAvatar pose="break" heightPx={190} loading="eager" />
        </div>
        <div style={styles.contentCol}>
        <h2 style={styles.heading}>Break Time</h2>
        <p style={styles.body}>
          Step away, stretch, hydrate. Your exam clock is paused and will
          resume automatically when the countdown ends.
        </p>
        <div
          style={{ ...styles.countdown, ...(warning ? styles.countdownWarning : {}) }}
          data-testid="break-countdown"
          aria-live="polite"
        >
          {formatBreakTime(secondsLeft)}
        </div>
        {pacePanel}
        <div style={styles.actions}>
          <button data-testid="resume-exam-button" style={styles.primaryButton} onClick={onResume}>
            Resume Exam
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}
